import { ChangeDetectorRef, Component, computed, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  ResolveEnd,
  Router,
} from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  mergeMap,
  pairwise,
  startWith,
  Subscription,
  tap,
  throwError,
} from 'rxjs';
import { getSession, getUserPreferences } from './auth/store/auth.actions';
import { selectUserPreferences } from './auth/store/auth.selectors';
import { IconRegistryService } from './core/icon-registry/icon-registry.service';
import { DialogService } from './core/services/dialog.service';
import { GlobalService } from './core/services/global.service';
import { Theme, ThemeService } from './core/services/theme.service';
import { TrackerService } from './core/services/tracker.service';
import { TranslationService } from './core/services/translationService';
import { onUntouched } from './shared/loading-state/loadable';
import { AppState } from './store/app.reducers';

export const APP_VERSION = '1.8.0';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ZEVA';

  private readonly subscription = new Subscription();
  protected readonly isNavigating = computed(() => !!this.router.currentNavigation());

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: Title,
    private readonly store: Store<AppState>,
    private readonly global: GlobalService,
    private readonly tracker: TrackerService,
    private readonly translationService: TranslationService,
    private readonly iconRegistry: IconRegistryService,
    private readonly dialogService: DialogService,
    private readonly transloco: TranslocoService,
    private readonly cdr: ChangeDetectorRef,
    protected readonly theme: ThemeService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(getSession());

    const firstUserPreferences$ = this.store.select(selectUserPreferences).pipe(
      onUntouched(() => this.store.dispatch(getUserPreferences())),
      tap(preferences => {
        if (!preferences.value || !preferences.value.appearance) {
          this.theme.setAuto();
        } else {
          let preference = preferences.value!.appearance;
          console.log('pref', preference);
  
          if (!preference || preference === 'auto') this.theme.setAuto();
          else this.theme.setTheme(preference as Theme);
        }

        this.cdr.markForCheck();
      })
    );
    this.subscription.add(firstUserPreferences$.subscribe());

    // Initialize SVG Icon Registry
    this.iconRegistry.init();

    // Initialize the dialog service
    void this.dialogService.init();

    /**
     * Common Angular Router events pipe that can be shared for different task
     * that base off of similar origin
     */
    const routerCommonPipe$ = this.router.events.pipe(
      // accept only emissions that happen at the end of navigation sequence
      filter(event => event instanceof NavigationEnd),
      // eslint-disable-next-line max-len
      // pass on the currently active route (Angular stores reference to the route in ActivatedRoute injectable)
      map(() => this.activatedRoute),
      map(route => {
        // loop through route children to find last activated route
        while (route.firstChild) route = route.firstChild;
        return route; // last activated route
      }),
      filter(route => route.outlet === 'primary')
    );

    /** extract router event with currently opened page's data from the route setup */
    const title$ = routerCommonPipe$.pipe(
      // filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data), // pull path's data containing 'title'
      tap(event => {
        // set returned path's title
        // eslint-disable-next-line prettier/prettier
        this.title = `${event['title'] as string} • ZEVA`;
        this.titleService.setTitle(this.title);
      })
    );
    this.subscription.add(title$.subscribe());

    /** collect and store immediate navigation to retain previous and current routes */
    const navigation$ = this.router.events.pipe(
      filter(evt => evt instanceof ResolveEnd),
      startWith(null),
      pairwise(),
      tap(events => {
        const [prev, next] = [events[0], events[1]] as [ResolveEnd | null, ResolveEnd];

        // omit same or similar links being recorded in succession
        if (prev !== null && next.url.includes(prev.url)) return;

        if (prev !== null) this.global.navigationState.previousRoute.next(prev);
        this.global.navigationState.currentRoute.next(next);

        // track page leave and page visit
        this._trackPageVisit(next, prev?.url);
      })
    );
    this.subscription.add(navigation$.subscribe());

    const language$ = this.translationService.getLanguage().pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
    this.subscription.add(language$.subscribe());
  }

  @HostListener('document:visibilitychange', [])
  onAppVisibilityChange(): void {
    // TODO: Does not work on app closure
    // TODO: until httpClient can send requests with keepalive parameter (Angular v20)
    // if (document.hidden) this.tracker.trackPageLeave()?.subscribe();
    // else if (this.global.navigationState.currentRoute.value)
    //   this._trackPageVisit(this.global.navigationState.currentRoute.value);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Traverse route's activated snapshot tree to get its title */
  private _getPageTitle(route: ResolveEnd): string {
    const maxDepth = 10;

    const extract = (page: ActivatedRouteSnapshot, depth: number): string | undefined => {
      if (page.title || page.data?.['title']) return page.title ?? page.data['title'];
      if (depth > maxDepth) return undefined;

      return extract(page.children[0], ++depth);
    };

    return extract(route.state.root, 0) ?? `Untitled, ${route.url}`;
  }

  private _trackPageVisit(route: ResolveEnd, previous_uri?: string): void {
    this.tracker
      .trackPageVisit(this._getPageTitle(route), undefined, previous_uri, false)
      .subscribe();
  }
}
